import { Mail, Phone, MapPin, Send, CheckCircle2, Clock, Users, Briefcase } from "lucide-react";

export default function GetInTouch() {
  return (
    <section
      className="w-full py-20 md:py-28 lg:py-32"
      style={{ backgroundColor: "#ffffff" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        
        {/* Header Section */}
        <div className="text-center mb-12">
          {/* Bubble tag */}
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-2 h-2 bg-black rounded-full"></div>
            <p 
              className="text-sm font-medium uppercase tracking-wider"
              style={{ 
                fontFamily: '"Open Sans", sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#081120'
              }}
            >
              Let's Connect
            </p>
          </div>

          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-4"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
              color: '#081120'
            }}
          >
            Get in Touch
          </h2>

          <p
            className="text-base max-w-2xl mx-auto text-center"
            style={{ 
              fontFamily: '"Open Sans", sans-serif',
              fontSize: '16px',
              lineHeight: '180%',
              color: '#555658'
            }}
          >
            Our team is ready to understand your unique hiring needs
          </p>
        </div>

        {/* Main Content - Split Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          
          {/* Left Side - Contact Information */}
          <div>
            {/* Office Address */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#e8f2fa' }}
                >
                  <MapPin className="w-5 h-5" style={{ color: '#0058a9' }} />
                </div>
                <h3
                  className="text-lg font-semibold"
                  style={{ 
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 600,
                    color: '#081120'
                  }}
                >
                  India Office
                </h3>
              </div>
              <div 
                className="rounded-xl p-5"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <p
                  className="text-sm leading-relaxed"
                  style={{ 
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: '14px',
                    lineHeight: '170%',
                    color: '#555658'
                  }}
                >
                  <span className="font-semibold" style={{ color: '#081120' }}>Kapil Tech</span><br />
                  Kapil Kavuri Hub, Nanakramguda,<br />
                  Gachibowli, Hyderabad<br />
                  Telangana - 500032
                </p>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center gap-4 p-4 rounded-xl transition-all duration-300 hover:translate-x-1" style={{ backgroundColor: '#f8f9fa' }}>
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#e8f2fa' }}
                >
                  <Mail className="w-5 h-5" style={{ color: '#0058a9' }} />
                </div>
                <div>
                  <p
                    className="text-xs font-medium uppercase tracking-wider mb-0.5"
                    style={{ color: '#9ca3af' }}
                  >
                    Email Us
                  </p>
                  <a
                    href="mailto:info@kapiltech.com"
                    className="text-base font-medium transition-colors duration-300 hover:underline"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      color: '#081120'
                    }}
                  >
                    info@kapiltech.com
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-4 p-4 rounded-xl transition-all duration-300 hover:translate-x-1" style={{ backgroundColor: '#f8f9fa' }}>
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: '#e8f2fa' }}
                >
                  <Phone className="w-5 h-5" style={{ color: '#0058a9' }} />
                </div>
                <div>
                  <p
                    className="text-xs font-medium uppercase tracking-wider mb-0.5"
                    style={{ color: '#9ca3af' }}
                  >
                    Contact Number
                  </p>
                  <a
                    href="tel:+918889992746"
                    className="text-base font-medium transition-colors duration-300 hover:underline"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      color: '#081120'
                    }}
                  >
                    +91 88899 92746
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Response Badge */}
            <div className="mt-8 p-4 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#f0fdfa', border: '1px solid #d1fae5' }}>
              <Clock className="w-5 h-5" style={{ color: '#0d9488' }} />
              <p className="text-sm" style={{ fontFamily: '"Open Sans", sans-serif', color: '#555658' }}>
                <span className="font-semibold" style={{ color: '#0d9488' }}>Quick Response:</span> We'll get back to you within 24 hours
              </p>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div>
            <div 
              className="rounded-2xl p-6 md:p-8"
              style={{ 
                backgroundColor: '#f8f9fa',
                border: '1px solid #eef2f6'
              }}
            >
              <h3
                className="text-xl font-semibold mb-2"
                style={{ 
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 600,
                  color: '#081120'
                }}
              >
                Send us a Message
              </h3>
              <p
                className="text-sm mb-6"
                style={{ 
                  fontFamily: '"Open Sans", sans-serif',
                  fontSize: '13px',
                  color: '#9ca3af'
                }}
              >
                Fill out the form and our team will contact you shortly
              </p>

              <form className="space-y-4">
                {/* Name Field */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#081120'
                    }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    className="w-full px-4 py-2.5 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      borderColor: '#e5e7eb',
                      backgroundColor: '#ffffff',
                      color: '#081120'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#0058a9';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 88, 169, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#081120'
                    }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    className="w-full px-4 py-2.5 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      borderColor: '#e5e7eb',
                      backgroundColor: '#ffffff',
                      color: '#081120'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#0058a9';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 88, 169, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Phone Field */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#081120'
                    }}
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    className="w-full px-4 py-2.5 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      borderColor: '#e5e7eb',
                      backgroundColor: '#ffffff',
                      color: '#081120'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#0058a9';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 88, 169, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Hiring Need Field */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#081120'
                    }}
                  >
                    Hiring Need *
                  </label>
                  <select
                    className="w-full px-4 py-2.5 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      borderColor: '#e5e7eb',
                      backgroundColor: '#ffffff',
                      color: '#081120'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#0058a9';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 88, 169, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select your hiring need</option>
                    <option value="permanent">Permanent Staffing</option>
                    <option value="contract">Contract/Temporary</option>
                    <option value="nearshoring">Nearshoring Solutions</option>
                    <option value="sourcing">Direct Sourcing</option>
                  </select>
                </div>

                {/* Message Field */}
                <div>
                  <label
                    className="block text-sm font-medium mb-1.5"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#081120'
                    }}
                  >
                    Message *
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Tell us about your requirements..."
                    className="w-full px-4 py-2.5 rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 resize-none"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      borderColor: '#e5e7eb',
                      backgroundColor: '#ffffff',
                      color: '#081120'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#0058a9';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 88, 169, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 group"
                  style={{ 
                    backgroundColor: '#081120',
                    transform: 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span
                    className="text-sm font-semibold"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#ffffff'
                    }}
                  >
                    Send Message
                  </span>
                  <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: '#ffffff' }} />
                </button>

                <p
                  className="text-xs text-center mt-4"
                  style={{ 
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: '11px',
                    color: '#9ca3af'
                  }}
                >
                  By submitting, you agree to our privacy policy. We'll never share your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}